import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  NavigationExtras,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { environment } from '@environments/environment';
import { CurrentTenantService } from '@suvo-bi-users';
import { lastValueFrom } from 'rxjs';

type TenantJourneyState = {
  shopSetupWizardCompleted?: boolean;
};

type TenantDatasourceDefinition = {
  _id?: string;
};

@Injectable()
export class TenantSelectedGuard {
  private readonly lastTenantStorageKey = 'lastSelectedTenantId';
  private readonly legacyLastTenantStorageKey = 'last-selected-tenant-id';

  constructor(
    private readonly http: HttpClient,
    private readonly currentTenantService: CurrentTenantService,
    private readonly router: Router,
  ) {}

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    // Is current tenant already set?
    if (this.currentTenantService.currentTenant()) {
      return this.ensureJourneyCompleted(state.url);
    }

    const lastSelectedTenantId = this.getStoredTenantId();
    if (lastSelectedTenantId) {
      try {
        await this.currentTenantService.init(
          `${environment.usersServiceApi}tenant/${lastSelectedTenantId}`,
        );

        if (this.currentTenantService.currentTenant()) {
          return this.ensureJourneyCompleted(state.url);
        }
      } catch {
        // Ignore and fallback to shop selection route.
      }
    }

    const queryParams: Record<string, string> = {
      redirectUrl: encodeURIComponent(state.url),
    };

    if (state.url.startsWith('/private/login-referrer')) {
      queryParams.autoRedirect = 'true';
    }

    const options: NavigationExtras = { queryParams };

    await this.router.navigate(['shops'], options);
    return false;
  }

  private async ensureJourneyCompleted(requestedUrl: string): Promise<boolean> {
    if (requestedUrl.startsWith('/private/journey')) {
      return true;
    }

    try {
      const journeyState = await lastValueFrom(
        this.http.get<TenantJourneyState>(`${environment.dataServiceApi}tenant-journey`),
      );

      if (!journeyState?.shopSetupWizardCompleted) {
        const hasDatasource = await this.hasExistingDatasource();
        if (hasDatasource) {
          await this.markJourneyCompleted();
          return true;
        }

        await this.router.navigate(['/private/journey']);
        return false;
      }
    } catch {
      // Do not block navigation on journey read failures.
    }

    return true;
  }

  private async hasExistingDatasource(): Promise<boolean> {
    try {
      const response = await lastValueFrom(
        this.http.get<{ data?: TenantDatasourceDefinition[] } | TenantDatasourceDefinition[]>(
          `${environment.dataServiceApi}definitions`,
        ),
      );

      const definitions = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : [];

      return definitions.length > 0;
    } catch {
      return false;
    }
  }

  private async markJourneyCompleted() {
    try {
      await lastValueFrom(
        this.http.put(`${environment.dataServiceApi}tenant-journey`, {
          shopSetupWizardCompleted: true,
        }),
      );
    } catch {
      // Best effort only; guard still allows navigation when datasource exists.
    }
  }

  private getStoredTenantId(): string | null {
    const currentKeyValue = localStorage.getItem(this.lastTenantStorageKey);
    if (currentKeyValue) {
      return currentKeyValue;
    }

    const legacyKeyValue = localStorage.getItem(this.legacyLastTenantStorageKey);
    if (!legacyKeyValue) {
      return null;
    }

    // Migrate old key to keep guard and shop-select behavior in sync.
    localStorage.setItem(this.lastTenantStorageKey, legacyKeyValue);
    return legacyKeyValue;
  }
}
